// src/components/PolotnoEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@blueprintjs/core";
import { createStore } from "polotno/model/store";
import { PolotnoContainer, SidePanelWrap, WorkspaceWrap } from "polotno";
import { Workspace } from "polotno/canvas/workspace";
import { Toolbar } from "polotno/toolbar/toolbar";
import { DEFAULT_SECTIONS, ImagesGrid, SectionTab, SidePanel } from "polotno/side-panel";
import { ZoomButtons } from 'polotno/toolbar/zoom-buttons';
import { PagesTimeline } from 'polotno/pages-timeline';
import { unstable_setAnimationsEnabled } from 'polotno/config';
import { clonePolotnoJson, rebuildPolotnoJsonSafely } from "../utils/polotnoJsonLoader";
import { getTemplateAssetRefs } from "../data/templateAssetRegistry";
import { getApiBaseUrl } from "../api/utils";
import "./PolotnoEditor.css";

unstable_setAnimationsEnabled(true);

const API_URL = getApiBaseUrl();
const POLOTNO_ALLOWED_DEFAULT_SECTIONS = DEFAULT_SECTIONS.filter(
  (section) => !["templates", "template", "photos", "videos"].includes(String(section?.name || section?.id || "").toLowerCase())
);
const HIDDEN_VIDEOS_SECTION = {
  name: "videos",
  Tab: () => null,
  Panel: () => null,
  visibleInList: false,
};

function getAssetDisplayName(asset) {
  return asset?.display_name || asset?.displayName || asset?.name || asset?.asset_key || "Template asset";
}

function collectMediaUrlsFromPolotnoJson(json) {
  const urls = new Set();
  const visit = (node) => {
    if (!node || typeof node !== "object") {
      return;
    }

    ["src", "url", "preview", "maskSrc", "clipSrc"].forEach((key) => {
      const value = node[key];
      if (typeof value === "string" && /^https?:\/\//i.test(value)) {
        urls.add(value);
      }
    });

    Object.values(node).forEach((value) => {
      if (Array.isArray(value)) {
        value.forEach(visit);
      } else if (value && typeof value === "object") {
        visit(value);
      }
    });
  };

  visit(json);
  return Array.from(urls);
}

function preloadImageUrl(url, timeoutMs = 12000) {
  return new Promise((resolve) => {
    const image = new Image();
    let didFinish = false;

    const finish = (status) => {
      if (didFinish) {
        return;
      }
      didFinish = true;
      clearTimeout(timeoutId);
      resolve({ url, status });
    };

    const timeoutId = setTimeout(() => finish("timeout"), timeoutMs);
    image.crossOrigin = "anonymous";
    image.onload = () => finish("loaded");
    image.onerror = () => finish("error");
    image.src = url;
  });
}

async function warmPolotnoMediaCache(json) {
  const urls = collectMediaUrlsFromPolotnoJson(json).filter(
    (url) => !/\.(mp4|webm|mov|mp3|wav|ogg|m4a)(\?|#|$)/i.test(url)
  );

  if (!urls.length) {
    return;
  }

  const startedAt = performance.now();
  const results = await Promise.all(urls.map((url) => preloadImageUrl(url)));
  const slowOrFailed = results.filter((result) => result.status !== "loaded");
  console.info("Preloaded Polotno media before editor mount", {
    count: urls.length,
    seconds: Number(((performance.now() - startedAt) / 1000).toFixed(2)),
    slowOrFailed,
  });
}

function isPlaceholderAssetUrl(url) {
  if (!url) {
    return true;
  }

  const normalizedUrl = url.trim().toLowerCase();
  return (
    normalizedUrl.startsWith("pending://") ||
    normalizedUrl.includes("/pending/") ||
    normalizedUrl.startsWith("/template-assets/generated/pending")
  );
}

function isFetchableMediaUrl(url) {
  if (!url || isPlaceholderAssetUrl(url)) {
    return false;
  }

  const normalizedUrl = url.trim().toLowerCase();
  return normalizedUrl.startsWith("http://") || normalizedUrl.startsWith("https://");
}

function normalizePolotnoMediaUrl(url) {
  if (!url || typeof url !== "string" || url.startsWith("data:") || isPlaceholderAssetUrl(url)) {
    return url;
  }

  const trimmedUrl = url.trim();
  const shouldRouteThroughApi = (pathname) =>
    pathname.startsWith("/templates/") || pathname.startsWith("/media/");

  if (trimmedUrl.startsWith("/") && shouldRouteThroughApi(trimmedUrl)) {
    return `${API_URL}${trimmedUrl}`;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    const isLocalApiUrl =
      parsedUrl.hostname === "localhost" ||
      parsedUrl.hostname === "127.0.0.1" ||
      parsedUrl.port === "8000";
    const isSameOriginAppUrl =
      typeof window !== "undefined" &&
      parsedUrl.origin === window.location.origin &&
      shouldRouteThroughApi(parsedUrl.pathname);

    if ((isLocalApiUrl || isSameOriginAppUrl) && shouldRouteThroughApi(parsedUrl.pathname)) {
      return `${API_URL}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }
  } catch (_error) {
    // Leave non-URL values untouched; Polotno can decide how to handle them.
  }

  return trimmedUrl;
}

function normalizePolotnoMediaUrls(json) {
  const nextJson = clonePolotnoJson(json);
  const mediaKeys = new Set(["src", "url", "preview", "maskSrc", "clipSrc"]);

  const visit = (node) => {
    if (!node || typeof node !== "object") {
      return;
    }

    Object.entries(node).forEach(([key, value]) => {
      if (mediaKeys.has(key) && typeof value === "string") {
        node[key] = normalizePolotnoMediaUrl(value);
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(visit);
      } else if (value && typeof value === "object") {
        visit(value);
      }
    });
  };

  visit(nextJson);
  return nextJson;
}

function getAssetMediaUrlForType(asset, previewUrl) {
  return [
    asset?.file_url,
    asset?.thumbnail_url,
    asset?.storage_path,
    previewUrl,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function getLibraryAssetMediaUrl(asset) {
  if (
    !asset?.id ||
    asset?.asset_metadata?.has_media === false ||
    !isFetchableMediaUrl(asset?.file_url)
  ) {
    return "";
  }

  const version = asset?.updated_at || asset?.asset_metadata?.generated_at || asset?.asset_metadata?.generated_size || "";
  const cacheBust = version ? `?v=${encodeURIComponent(version)}` : "";
  const mediaUrl = asset?.asset_metadata?.media_url;
  if (mediaUrl && !isPlaceholderAssetUrl(mediaUrl)) {
    const absoluteUrl = mediaUrl.startsWith("http") ? mediaUrl : `${API_URL}${mediaUrl}`;
    return `${absoluteUrl}${absoluteUrl.includes("?") ? "&" : cacheBust ? "?" : ""}${cacheBust.replace("?", "")}`;
  }

  return `${API_URL}/templates/asset-library/${asset.id}/media${cacheBust}`;
}

function getAssetPreviewUrl(asset) {
  if (asset?.thumbnail_url || asset?.file_url) {
    return asset.thumbnail_url || asset.file_url;
  }

  const registryAsset = getTemplateAssetRefs(asset?.family_id)?.[asset?.role];
  return registryAsset?.src || "";
}

function getPolotnoPreviewUrl(asset, assetScope) {
  if (assetScope === "library") {
    return getLibraryAssetMediaUrl(asset);
  }

  if (asset?.library_asset_id && !isPlaceholderAssetUrl(asset?.file_url)) {
    return `${API_URL}/templates/asset-library/${asset.library_asset_id}/media`;
  }

  return getAssetPreviewUrl(asset);
}

function getPolotnoAssetType(asset, previewUrl) {
  const type = (asset?.asset_type || "").toLowerCase();
  const url = getAssetMediaUrlForType(asset, previewUrl);

  if (/\.(mp4|webm|mov)(\?|#|\s|$)/.test(url) || type.includes("video")) {
    return "video";
  }

  if (/\.(mp3|wav|ogg|m4a)(\?|#|\s|$)/.test(url) || type.includes("audio")) {
    return "audio";
  }

  if (/\.(png|jpe?g|webp|gif)(\?|#|\s|$)/.test(url)) {
    return "image";
  }

  if (/\.svg(\?|#|\s|$)/.test(url) || type.includes("svg")) {
    return "svg";
  }

  return "image";
}

function getAssetPlacement(asset) {
  return asset?.polotno_mapping?.defaultPlacement || {};
}

function getAssetCustomMetadata(asset, assetScope) {
  if (assetScope === "library") {
    return {
      library_asset_id: asset.id,
    };
  }

  return {
    template_asset_id: asset.id,
    template_asset_role: asset.role,
    template_asset_key: asset.asset_key,
    library_asset_id: asset.library_asset_id,
  };
}

function applyAssetMetadataToLatestElement(store, targetElement, asset, assetScope) {
  const metadata = getAssetCustomMetadata(asset, assetScope);

  if (targetElement?.set) {
    targetElement.set({
      custom: {
        ...(targetElement.custom || {}),
        ...metadata,
      },
    });
    return;
  }

  const page = store.activePage || store.pages?.[0];
  const latestElement = page?.children?.[page.children.length - 1];
  if (!latestElement?.set) {
    return;
  }

  latestElement.set({
    custom: {
      ...(latestElement.custom || {}),
      ...metadata,
    },
  });
}

function TemplateAssetsPanel({ store, templateAssets, libraryAssets, isAdmin = false }) {
  const canUseTemplateScope = isAdmin && templateAssets.length > 0;
  const [assetScope, setAssetScope] = useState(canUseTemplateScope ? "template" : "library");
  const [showReviewAssets, setShowReviewAssets] = useState(false);
  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  useEffect(() => {
    if (!canUseTemplateScope && assetScope === "template") {
      setAssetScope("library");
    }
  }, [assetScope, canUseTemplateScope]);
  const normalizedSearchQuery = assetSearchQuery.trim().toLowerCase();
  const activeAssets =
    assetScope === "library" && !showReviewAssets
      ? libraryAssets.filter((asset) => asset.status === "available")
      : assetScope === "library"
        ? libraryAssets
        : templateAssets;
  const hiddenReviewCount =
    assetScope === "library" && !showReviewAssets
      ? libraryAssets.filter((asset) => asset.status && asset.status !== "available").length
      : 0;
  const searchableAssets = activeAssets.filter((asset) => {
    if (!normalizedSearchQuery) {
      return true;
    }

    const searchableText = [
      getAssetDisplayName(asset),
      asset?.name,
      asset?.asset_key,
      asset?.assetKey,
      asset?.role,
      asset?.asset_type,
      asset?.assetType,
      asset?.status,
      asset?.description,
      asset?.prompt_reference,
      asset?.generation_prompt,
      ...(asset?.usage_tags || []),
      ...(asset?.tags || []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedSearchQuery);
  });
  const visibleAssets = searchableAssets.filter((asset) => {
    const previewUrl = getPolotnoPreviewUrl(asset, assetScope);
    return (
      previewUrl &&
      !isPlaceholderAssetUrl(previewUrl) &&
      getPolotnoAssetType(asset, previewUrl) !== "audio"
    );
  });
  const audioAssets = searchableAssets.filter((asset) => {
    const previewUrl = getPolotnoPreviewUrl(asset, assetScope);
    return previewUrl && !isPlaceholderAssetUrl(previewUrl) && getPolotnoAssetType(asset, previewUrl) === "audio";
  });

  const addAudioToTimeline = (asset) => {
    const previewUrl = getPolotnoPreviewUrl(asset, assetScope);
    if (!store || !previewUrl || !store.addAudio) {
      return;
    }

    const existingAudio = (store.audios || []).find((audio) => audio.src === previewUrl);
    if (existingAudio) {
      window.alert("This music track is already added to the timeline.");
      return;
    }

    store.addAudio({
      src: previewUrl,
      name: getAssetDisplayName(asset),
      delay: 0,
      startTime: 0,
      endTime: 1,
      volume: 1,
      duration: Math.max(Number(store.duration) || 5000, 5000),
    });
  };

  const addAssetToPage = async (asset, droppedPos, targetElement) => {
    const previewUrl = getPolotnoPreviewUrl(asset, assetScope);
    if (!store || !previewUrl) {
      return;
    }

    if (!store.activePage) {
      store.addPage();
    }

    const page = store.activePage || store.pages?.[0];
    if (!page) {
      return;
    }

    const assetType = getPolotnoAssetType(asset, previewUrl);
    if (assetType === "audio") {
      addAudioToTimeline(asset);
      return;
    }

    const canvasSrc = previewUrl;

    try {
      const placement = getAssetPlacement(asset);
      const loadNaturalSize = () =>
        new Promise((resolve) => {
          if (assetType !== "image" && assetType !== "svg") {
            resolve(null);
            return;
          }

          const image = new Image();
          image.onload = () =>
            resolve({
              width: image.naturalWidth || image.width,
              height: image.naturalHeight || image.height,
            });
          image.onerror = () => resolve(null);
          image.src = canvasSrc;
        });

      const naturalDimensions =
        assetType === "image" || assetType === "svg"
          ? await loadNaturalSize()
          : null;
      const placementWidth = Number(placement.width);
      const placementHeight = Number(placement.height);
      const naturalWidth = Number(naturalDimensions?.width);
      const naturalHeight = Number(naturalDimensions?.height);
      const aspectRatio = naturalWidth > 0 && naturalHeight > 0 ? naturalWidth / naturalHeight : 1;
      const maxSize = 620;
      const fallbackSize = 520;
      let width = placementWidth > 0 ? placementWidth : null;
      let height = placementHeight > 0 ? placementHeight : null;

      if (!width && !height) {
        width = aspectRatio >= 1 ? maxSize : Math.max(1, Math.round(fallbackSize * aspectRatio));
        height = aspectRatio >= 1 ? Math.max(1, Math.round(maxSize / aspectRatio)) : fallbackSize;
      } else if (width && !height) {
        height = Math.max(1, Math.round(width / aspectRatio));
      } else if (!width && height) {
        width = Math.max(1, Math.round(height * aspectRatio));
      }

      const x = droppedPos?.x ? droppedPos.x - width / 2 : placement.x ?? 120;
      const y = droppedPos?.y ? droppedPos.y - height / 2 : placement.y ?? 160;

      page.addElement({
        type: assetType,
        name: `asset:${asset.role || asset.asset_key || asset.name}`,
        src: canvasSrc,
        x,
        y,
        width,
        height,
        opacity: placement.opacity ?? 1,
        rotation: placement.rotation ?? 0,
        cropX: 0,
        cropY: 0,
        cropWidth: 1,
        cropHeight: 1,
        keepRatio: true,
        maskSrc: "",
        flipX: false,
        flipY: false,
        borderColor: "rgba(0,0,0,0)",
        borderSize: 0,
        cornerRadius: 0,
      });

      applyAssetMetadataToLatestElement(store, targetElement, asset, assetScope);
    } catch (error) {
      console.error("Failed to add asset to Polotno canvas:", {
        error,
        asset,
        assetType,
        previewUrl,
        canvasSrcPrefix: typeof canvasSrc === "string" ? canvasSrc.slice(0, 80) : "",
      });
      window.alert("Could not add this asset to the editor. Please check the browser console for details.");
      return;
    }
  };

  return (
    <div className="polotno-template-assets-panel">
      <div className="polotno-template-assets-header">
        <p>Assets</p>
        <span>
          {visibleAssets.length}/{activeAssets.length}
          {hiddenReviewCount ? ` · ${hiddenReviewCount} in review` : ""}
        </span>
      </div>

      {isAdmin ? (
        <div className="polotno-template-assets-scope" role="tablist" aria-label="Asset scope">
          <button
            type="button"
            className={assetScope === "template" ? "active" : ""}
            onClick={() => setAssetScope("template")}
            disabled={!templateAssets.length}
          >
            This Template
          </button>
          <button
            type="button"
            className={assetScope === "library" ? "active" : ""}
            onClick={() => setAssetScope("library")}
          >
            Library
          </button>
        </div>
      ) : null}

      <div className="polotno-template-assets-search">
        <input
          type="search"
          value={assetSearchQuery}
          onChange={(event) => setAssetSearchQuery(event.target.value)}
          placeholder={assetScope === "library" ? "Search library assets" : "Search template assets"}
        />
        {assetSearchQuery ? (
          <button type="button" onClick={() => setAssetSearchQuery("")} aria-label="Clear asset search">
            Clear
          </button>
        ) : null}
      </div>

      {isAdmin && assetScope === "library" ? (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "10px 0",
            fontSize: 12,
            color: "#6f4a1f",
            fontWeight: 700,
          }}
        >
          <input
            type="checkbox"
            checked={showReviewAssets}
            onChange={(event) => setShowReviewAssets(event.target.checked)}
          />
          Include needs-review assets
        </label>
      ) : null}
      {assetScope === "library" && hiddenReviewCount && !showReviewAssets ? (
        <div className="polotno-template-assets-note">
          {hiddenReviewCount} regenerated or review asset{hiddenReviewCount === 1 ? " is" : "s are"} hidden.
          Turn on "Include needs-review assets" or approve them in Admin Assets.
        </div>
      ) : null}

      {activeAssets.length ? (
        <div className="polotno-template-assets-grid-wrap">
          <p className="polotno-template-assets-tip">
            {normalizedSearchQuery
              ? `${visibleAssets.length} visual result${visibleAssets.length === 1 ? "" : "s"} for "${assetSearchQuery.trim()}".`
              : "Click an asset to place it, or drag it onto the canvas."}
          </p>
          {visibleAssets.length ? (
          <ImagesGrid
            images={visibleAssets}
            isLoading={false}
            hideNoResults={false}
            rowsNumber={2}
            itemHeight={128}
            shadowEnabled={true}
            getPreview={(asset) => getPolotnoPreviewUrl(asset, assetScope)}
            getCredit={(asset) => (
              <span>
                {getAssetDisplayName(asset)}
                {asset.status ? ` · ${asset.status}` : ""}
              </span>
            )}
            onSelect={(asset, droppedPos, targetElement) =>
              addAssetToPage(asset, droppedPos, targetElement)
            }
          />
          ) : (
            <div className="polotno-template-assets-empty">
              No visual assets matched this search.
            </div>
          )}
          {activeAssets.some((asset) => {
            const previewUrl = getPolotnoPreviewUrl(asset, assetScope);
            return !previewUrl || getPolotnoAssetType(asset, previewUrl) === "audio";
          }) ? (
            <div className="polotno-template-assets-note">
              Some assets do not have a visual URL yet.
            </div>
          ) : null}
          {audioAssets.length ? (
            <div className="polotno-template-audio-assets">
              <p className="polotno-template-assets-tip">
                Music tracks are added to the Polotno timeline.
              </p>
              {audioAssets.map((asset) => (
                <button
                  key={asset.id || asset.asset_key || asset.name}
                  type="button"
                  className="polotno-template-audio-asset"
                  onClick={() => addAudioToTimeline(asset)}
                >
                  <span>{getAssetDisplayName(asset)}</span>
                  <small>{asset.status ? `${asset.status} · ` : ""}Add music</small>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="polotno-template-assets-empty">
          {assetScope === "library"
            ? "No reusable library assets matched this view."
            : "No saved template assets yet."}
        </div>
      )}
    </div>
  );
}

function createAdminAssetsSection({ templateAssets, assetLibraryItems }) {
  return {
    name: "admin-assets",
    Tab: (props) => (
      <SectionTab name="Assets" {...props}>
        <Icon icon="media" />
      </SectionTab>
    ),
    Panel: ({ store }) => (
      <TemplateAssetsPanel
        store={store}
        templateAssets={templateAssets}
        libraryAssets={assetLibraryItems}
        isAdmin
      />
    ),
  };
}

function createAssetsSection({ assetLibraryItems }) {
  return {
    name: "desiinvite-assets",
    Tab: (props) => (
      <SectionTab name="Assets" {...props}>
        <Icon icon="media" />
      </SectionTab>
    ),
    Panel: ({ store }) => (
      <TemplateAssetsPanel
        store={store}
        templateAssets={[]}
        libraryAssets={assetLibraryItems}
        isAdmin={false}
      />
    ),
  };
}

export default function PolotnoEditor({
  template,
  onStoreReady,
  initialJson,
  templateAssets = [],
  assetLibraryItems = [],
  showTemplateAssetsPanel = false,
  isAdminAssetPanel = false,
}) {
  const [store, setStore] = useState(null);
  const sidePanelSections = useMemo(() => {
    if (!showTemplateAssetsPanel) {
      return [...POLOTNO_ALLOWED_DEFAULT_SECTIONS, HIDDEN_VIDEOS_SECTION];
    }

    const assetsSection = isAdminAssetPanel
      ? createAdminAssetsSection({ templateAssets, assetLibraryItems })
      : createAssetsSection({ assetLibraryItems });

    return [
      assetsSection,
      ...POLOTNO_ALLOWED_DEFAULT_SECTIONS,
      HIDDEN_VIDEOS_SECTION,
    ];
  }, [assetLibraryItems, isAdminAssetPanel, showTemplateAssetsPanel, templateAssets]);

  useEffect(() => {
    let isMounted = true;
    const newStore = createStore({ key: "demo-key" });

    const resetEditorPlaybackState = () => {
      try {
        if (newStore.isPlaying && typeof newStore.stop === "function") {
          newStore.stop();
        } else if (typeof newStore.setCurrentTime === "function") {
          newStore.setCurrentTime(0);
        }

        const firstPageId = newStore.pages?.[0]?.id;
        if (firstPageId && typeof newStore.selectPage === "function") {
          newStore.selectPage(firstPageId);
        }

        if (newStore.isPlaying) {
          newStore._togglePlaying?.(false);
        }
      } catch (playbackResetError) {
        console.warn("Unable to reset Polotno playback state after editor load.", playbackResetError);
      }
    };

    const ensureVisiblePage = () => {
      if (newStore.pages.length > 0) {
        return;
      }

      newStore.addPage({
        id: "fallback-template-page",
        width: "auto",
        height: "auto",
        background: "rgba(82, 24, 42, 1)",
        bleed: 0,
        duration: 5000,
        children: [
          {
            id: "fallback-template-title",
            type: "text",
            text: "Royal Wedding Draft",
            x: 150,
            y: 280,
            width: 780,
            height: 120,
            fontSize: 68,
            fontFamily: "Georgia",
            fontStyle: "normal",
            fontWeight: "bold",
            fill: "rgba(245, 234, 214, 1)",
            align: "center",
            lineHeight: 1.2,
            letterSpacing: 0,
          },
          {
            id: "fallback-template-subtitle",
            type: "text",
            text: "Add or regenerate scenes from the admin template flow.",
            x: 180,
            y: 460,
            width: 720,
            height: 100,
            fontSize: 34,
            fontFamily: "Georgia",
            fontStyle: "normal",
            fontWeight: "normal",
            fill: "rgba(233, 214, 174, 1)",
            align: "center",
            lineHeight: 1.25,
            letterSpacing: 0,
          },
        ],
      });
    };

    const initializeStore = async () => {
      try {
        if (initialJson) {
          try {
            console.info("Loading initial Polotno draft JSON", {
              pages: Array.isArray(initialJson.pages) ? initialJson.pages.length : 0,
            });
            const normalizedInitialJson = normalizePolotnoMediaUrls(initialJson);
            await warmPolotnoMediaCache(normalizedInitialJson);
            await Promise.resolve(newStore.loadJSON(clonePolotnoJson(normalizedInitialJson)));
          } catch (initialLoadError) {
            console.error("Failed to restore editor draft JSON natively, trying safe rebuild.", initialLoadError);
            const normalizedInitialJson = normalizePolotnoMediaUrls(initialJson);
            if (!rebuildPolotnoJsonSafely(newStore, normalizedInitialJson) && template?.htmltext) {
              const templateJson = normalizePolotnoMediaUrls(JSON.parse(template.htmltext));
              console.info("Loading template Polotno JSON after draft failure", {
                pages: Array.isArray(templateJson.pages) ? templateJson.pages.length : 0,
              });
              try {
                await Promise.resolve(newStore.loadJSON(clonePolotnoJson(templateJson)));
              } catch (templateLoadError) {
                console.error("Failed to load template JSON after draft failure.", templateLoadError);
                rebuildPolotnoJsonSafely(newStore, templateJson);
              }
            }
          }
        } else if (template?.htmltext) {
          const templateJson = normalizePolotnoMediaUrls(JSON.parse(template.htmltext));
          console.info("Loading template Polotno JSON", {
            pages: Array.isArray(templateJson.pages) ? templateJson.pages.length : 0,
          });
          await warmPolotnoMediaCache(templateJson);
          try {
            await Promise.resolve(newStore.loadJSON(clonePolotnoJson(templateJson)));
          } catch (templateLoadError) {
            console.error("Failed to load template JSON natively, trying safe rebuild.", templateLoadError);
            rebuildPolotnoJsonSafely(newStore, templateJson);
          }
        }
        ensureVisiblePage();
      } catch (loadError) {
        console.error("Failed to initialize Polotno editor:", loadError);
        ensureVisiblePage();
      } finally {
        resetEditorPlaybackState();
        if (isMounted) {
          onStoreReady(newStore);
          setStore(newStore);
        }
      }
    };

    initializeStore();

    return () => {
      isMounted = false;
      newStore.dispose?.();
    };
  }, [initialJson, template, onStoreReady]);

  // While store initializes, show a loader
  if (!store) return <div>Loading editor...</div>;

  return (
    <div className="polotno-editor-shell">
      <PolotnoContainer style={{ flex: 1, width: "100%", height: "100%", minWidth: 0, minHeight: 0 }}>
          <SidePanelWrap style={{ flex: "0 0 auto", minWidth: 0, position: "relative", zIndex: 4 }}>
            <SidePanel
              store={store}
              sections={sidePanelSections}
              defaultSection={showTemplateAssetsPanel ? (isAdminAssetPanel ? "admin-assets" : "desiinvite-assets") : "text"}
            />
          </SidePanelWrap>
          <WorkspaceWrap style={{ flex: "1 1 auto", width: "auto", minWidth: 0, minHeight: 0 }}>
            <Toolbar store={store} />
            <Workspace
              store={store}
              renderOnlyActivePage={false}
              visiblePagesOffset={store.pages.length}
            />
            <ZoomButtons store={store} />
            <PagesTimeline store={store} />
          </WorkspaceWrap>
        </PolotnoContainer>
    </div>
    
  );
}
